import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useLazyQuery } from "@apollo/client";
import * as _ from "lodash";
import { useToast } from "@npm-telluria-tecnologia/telluria-ui/dist";

// region Molecules
import { ClientSelectorOffice, Navbar, NewVersionBanner, Sidebar } from "@molecules/index";
import { ModuleIds, ModuleNames } from "@molecules/SidebarMenus";
// endregion Molecules
// region Store
import Store from "@store/jotai";
// endregion Store
// region Hooks
import { useWindowDimensions } from "@hooks/useWindowDimensions";
import { useSelectClient } from "@atoms/SelectClient";
import { useAuth } from "@hooks/useAuth";
// endregion Hooks
// region Graphql
import {
  ClientType,
  ECommandResultStatus,
  EParameter,
  EWhereClausesOperators,
  ParamQueriesGetAllWithPaginationArgs,
  PermissionUserQueriesGetAllWithoutPaginationArgs,
  PermissionUserType
} from "@store/graphql/helpers/types.graphql";
import { paramAllWithPagination, TParamAllWithPaginationQueries } from "@graphql/groups/param/query.graphql";
import {
  permissionUserAllWithoutPagination,
  TPermissionAllWithoutPaginationQueries
} from "@graphql/groups/permissionUser/query.graphql";
// endregion Graphql
// region Styles
import * as Styled from "./styles";

// endregion Styles

// Define types for PermissionsContext
export interface FormattedPermissions {
  [clientId: string]: {
    modules: string[];
    moduleIds: string[];
  };
}

interface PermissionsContextType {
  permissions: PermissionUserType[] | null;
  formattedPermissions: FormattedPermissions | null;
}

// Create PermissionsContext
const PermissionsContext = createContext<PermissionsContextType | undefined>(undefined);

// Hook to access PermissionsContext
export const usePermissions = () => {
  const context = useContext(PermissionsContext);

  if (!context) {
    throw new Error("usePermissions must be used within a PermissionsProvider");
  }

  return context;
};

interface OfficeProps {
  showSelectClient?: boolean;
  /**
   * When true, the content area fills the available height without its own
   * scroll (and hides the client selector bar), letting the page manage its
   * own internal scrolling. Used by the documentation page.
   */
  fillContent?: boolean;
}

const Office: React.FC<OfficeProps> = ({
  children,
  showSelectClient = true,
  fillContent = false
}) => {

  const { addToast } = useToast();
  const { width } = useWindowDimensions();
  const { getDecodedToken, signOut, user } = useAuth();

  const token = getDecodedToken();

  const [permissions, setPermissions] = useState<PermissionUserType[] | null>(null);
  const [formattedPermissions, setFormattedPermissions] = useState<FormattedPermissions | null>(null);

  const { setValue: setSelectedClient, value: selectedClient } = useSelectClient();
  const { value: clients, setValue: setClients } = Store.useClients();

  const [getParams] = useLazyQuery<
    TParamAllWithPaginationQueries,
    ParamQueriesGetAllWithPaginationArgs
  >(paramAllWithPagination(["id", "name", "key"]), {
    fetchPolicy: "network-only"
  });

  const [getPermissionUser, { loading, error, data: dataClients }] = useLazyQuery<
    TPermissionAllWithoutPaginationQueries,
    PermissionUserQueriesGetAllWithoutPaginationArgs
    >(
      permissionUserAllWithoutPagination([
        "construction.client.id",
        "construction.client.name",
        "construction.client.alias",
        "construction.client.document",
        "construction.client.creditLimit",
        "construction.client.creditLimitUsed",
        "construction.client.creditLimitExpiration",
        "construction.client.pendency",
        "construction.client.externalId",
        "contract.id",
        "module.id",
        "module.description"
      ]),
      {
        fetchPolicy: "network-only",
        variables: {
          where: [
            {
              property: "user.id",
              operator: EWhereClausesOperators.EQUALS,
              value: token.primarysid
            }
            // ,
            // {
            //   property: "user.isValidated",
            //   operator: EWhereClausesOperators.EQUALS,
            //   value: "true"
            // }
          ]
        }
      }
    );

  const formatPermissions = useCallback((permissions) => {
    const formatted: FormattedPermissions = {};

    for (const permission of permissions) {
      const clientId = permission.construction.client.id;
      const permissionModuleDescription = permission.module.description as keyof typeof ModuleIds;
      const moduleId = ModuleIds[permissionModuleDescription];
      const moduleLink = ModuleNames[permissionModuleDescription];

      if (!formatted[clientId]) {
        formatted[clientId] = { modules: [moduleLink], moduleIds: [moduleId] };
      } else {
        formatted[clientId].modules.push(moduleLink);
        formatted[clientId].moduleIds.push(moduleId);
      }
    }

    return formatted;
  }, [user.admin]);

  /**
   * Load clients
   */
  const loadClients = useCallback(async () => {
    const response = dataClients?.permissionUser.getAllWithoutPagination;

    if (response?.status === ECommandResultStatus.SUCCESS) {
      const clientsArray = _.uniqBy(
        response?.result?.map((el) => el?.construction?.client),
        "id"
      ).sort((a, b) => {
        if (!a?.name || !b?.name) return 0;
        if (a.name < b.name) return -1;
        if (a.name > b.name) return 1;

        return 0;
      });

      setClients(clientsArray as ClientType[]);

      if (selectedClient.name === "" && clientsArray?.[0]) {
        setSelectedClient(clientsArray[0]);
      }

      // Has to validate admin as string and not boolean
      const isAdmin = (token?.admin ?? "").toLowerCase() === "true";

      if (clientsArray.length === 0 && !isAdmin) {
        // eslint-disable-next-line no-alert
        alert("Usuário sem permissões vinculadas!");
        signOut();
      }
    }
  }, [
    dataClients?.permissionUser.getAllWithoutPagination,
    setClients,
    selectedClient.name,
    token.admin,
    setSelectedClient,
    signOut
  ]);

  useEffect(() => {
    if (dataClients) {
      const rawPermissions = dataClients.permissionUser.getAllWithoutPagination?.result || [];

      setPermissions(rawPermissions as PermissionUserType[]);
      setFormattedPermissions(formatPermissions(rawPermissions));
    }
  }, [dataClients, formatPermissions]);

  // Load clients when data clients changes
  useEffect(() => {
    if (dataClients) loadClients().then();
    if (error) {
      addToast({
        type: "error",
        title: "Internal client error",
        description: error.message
      });
    }
  }, [loadClients, dataClients, error, addToast]);

  // Get permission of user
  useEffect(() => {
    getPermissionUser().then();
  }, [getPermissionUser]);

  useEffect(() => {
    if (dataClients !== undefined) {
      setPermissions(dataClients.permissionUser.getAllWithoutPagination.result as PermissionUserType[]);
    }
  }, [user.email, dataClients]);

  // Get client params (DB)
  useEffect(() => {

    async function getGoogleApiKey() {

      const { data } = await getParams({
        variables: {
          where: [
            {
              property: "name",
              operator: EWhereClausesOperators.EQUALS,
              value: EParameter.GOOGLE
            }
          ]
        }
      });

      if (data?.param.getAllWithPagination.status === "SUCCESS") {

        const result = data?.param.getAllWithPagination.result;

        if (result && result?.length > 0) {
          localStorage.setItem("@Topcon:google", result[0]?.key || "");
        }
      }
    }

    if (!localStorage.getItem("@Topcon:google")) getGoogleApiKey().then();
  }, [getParams]);

  const value = useMemo(() => ({
    permissions,
    formattedPermissions
  }), [permissions, formattedPermissions]);

  return (
    <Styled.OfficeContainer>
      <PermissionsContext.Provider value={value}>
        <Styled.OfficeNavbarContainer>
          <Navbar showSelectClient={showSelectClient} />
          <NewVersionBanner />
        </Styled.OfficeNavbarContainer>
        <Styled.OfficeContentContainer>
          <Sidebar />
          <Styled.OfficeContent id="officeContent" $fill={fillContent}>
            {width >= 1280 && !fillContent && (
              <ClientSelectorOffice showSelectClient={showSelectClient} />
            )}
            {children}
          </Styled.OfficeContent>
        </Styled.OfficeContentContainer>
      </PermissionsContext.Provider>
    </Styled.OfficeContainer>
  );
};

Office.defaultProps = {
  showSelectClient: true,
  fillContent: false
};

export default Office;